import React, { useEffect, useState } from 'react';
import { fetchGet } from '../../api/common';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { track } from '../../api/track';
import { useNavigate } from 'react-router-dom';
import { resizeImage } from '../../api/cloudinary';

export interface RankedUser {
  avatarPublicId: string;
  avatarUrl: string;
  profileId: string;
  username: string;
  rankedRating: number;
  rankedMatchGamesPlayed: number;
  rankedMatchRating: number;
  rankedMatchWins: number;
  rankedMatchLosses: number;
  rankedSitAndGoGamesPlayed: number;
  rankedSitAndGoRating: number;
}

export interface SurvivalRankedUser {
  avatarPublicId: string;
  avatarUrl: string;
  profileId: string;
  username: string;
  survivalStats: {
    endurance: {
      bestScore: {
        score: number;
        questionsSurvived: number;
      };
      gamesPlayed: number;
      longestStreak: {
        score: number;
        questionsSurvived: number;
      };
    };
    challenge: {
      daily: {
        score: number;
        questionsSurvived: number;
        playedAt: number;
      };
      allTime: {
        bestScore: {
          score: number;
          questionsSurvived: number;
        };
        gamesPlayed: number;
        longestStreak: {
          score: number;
          questionsSurvived: number;
        };
      };
    };
  };
}

export type DailyChallengeTowerUser = {
  createdAt: number;
  dailyChallengeTower: number;
  gameId: string;
  parScore: number;
  profileId: string;
  questionsSurvived: number;
  avatarPublicId: string;
  username: string;
};

type PlayerSeedData = {
  totalSeeded: number;
  totalSeededInRange: number;
  totalGamesInRange: number;
};

type PropTypes = {
  isAdmin: boolean;
};

export const Leaderboards = ({ isAdmin }: PropTypes) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rankedRankings, setRankedRankings] = useState<RankedUser[]>();
  const [survivalRankings, setSurvivalRankings] =
    useState<SurvivalRankedUser[]>();
  const [survivalRankingsLowestScoreSort, setSurvivalRankingsLowestScoreSort] =
    useState<SurvivalRankedUser[]>();
  const [
    survivalRankingsLongestStreakSort,
    setSurvivalRankingsLongestStreakSort,
  ] = useState<SurvivalRankedUser[]>();
  const [playerSeedData, setPlayerSeedData] =
    useState<Record<string, PlayerSeedData>>();
  const [tabValue, setTabValue] = useState<'ranked' | 'survival'>('ranked');
  const [survivalSelect, setSurvivalSelect] = useState<
    'endurance' | 'challenge'
  >('challenge');

  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: 'ranked' | 'survival'
  ) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (!isAdmin) return;
    fetchPlayerSeedData();
  }, [isAdmin]);

  useEffect(() => {
    if (tabValue === 'ranked') {
      fetchRankedData();
      return;
    }
    if (tabValue === 'survival') {
      fetchSurvivalChallengeData();
      return;
    }
  }, [tabValue]);

  useEffect(() => {
    if (survivalSelect === 'endurance') {
      fetchSurvivalEnduranceData();
      return;
    }
    if (survivalSelect === 'challenge') {
      fetchSurvivalChallengeData();
      return;
    }
  }, [survivalSelect]);

  const fetchRankedData = async () => {
    const res: RankedUser[] = await fetchGet(`/rankings/ranked`);
    if (res) {
      const sorted = res.sort((a, b) => b.rankedRating - a.rankedRating);
      setRankedRankings(sorted);
    }
  };

  const fetchSurvivalEnduranceData = async () => {
    const res: SurvivalRankedUser[] = await fetchGet(
      `/rankings/survival/endurance`
    );
    setSurvivalRankings(res);
    const sortedLowestScore = res
      .slice()
      .sort(
        (a, b) =>
          a.survivalStats.endurance.bestScore.score -
          b.survivalStats.endurance.bestScore.score
      );
    setSurvivalRankingsLowestScoreSort(sortedLowestScore);
    const sortedLongestStreak = res
      .slice()
      .sort(
        (a, b) =>
          b.survivalStats.endurance.longestStreak.questionsSurvived -
          a.survivalStats.endurance.longestStreak.questionsSurvived
      );
    setSurvivalRankingsLongestStreakSort(sortedLongestStreak);
  };

  const fetchSurvivalChallengeData = async () => {
    const res: SurvivalRankedUser[] = await fetchGet(
      `/rankings/survival/challenge/all-time`
    );
    setSurvivalRankings(res);
    const sortedLowestScore = res
      .slice()
      .sort(
        (a, b) =>
          a.survivalStats.challenge.allTime.bestScore.score -
          b.survivalStats.challenge.allTime.bestScore.score
      );
    setSurvivalRankingsLowestScoreSort(sortedLowestScore);
    const sortedLongestStreak = res
      .slice()
      .sort(
        (a, b) =>
          b.survivalStats.challenge.allTime.longestStreak.questionsSurvived -
          a.survivalStats.challenge.allTime.longestStreak.questionsSurvived
      );
    setSurvivalRankingsLongestStreakSort(sortedLongestStreak);
  };

  const fetchPlayerSeedData = async () => {
    const res = await fetchGet('/match-making/admin/player-seed-totals/');
    console.log('fetchPlayerSeedData', res);
    setPlayerSeedData(res);
  };

  const handleUsernameClick = async (e: any, player: RankedUser) => {
    e.stopPropagation();
    await track(player.profileId, 'rankings.username.link', player.username);
    navigate(`/profile/overview/${player.profileId}`);
  };

  const handleSurvivalSelectChange = (event: SelectChangeEvent) => {
    setSurvivalSelect(event.target.value as 'endurance' | 'challenge');
  };

  return (
    <Container sx={{ paddingBottom: '100px' }}>
      <Typography variant="h4" textAlign={'center'} letterSpacing={2} mb={2}>
        Leaderboards
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="secondary"
          indicatorColor="secondary"
        >
          <Tab value="ranked" label="Ranked" />
          <Tab value="survival" label="Survival" />
        </Tabs>
      </Box>

      {tabValue === 'ranked' ? (
        <TableContainer component={Paper}>
          {MainTable(
            handleUsernameClick,
            theme.palette.ranked.main,
            playerSeedData,
            rankedRankings
          )}
        </TableContainer>
      ) : (
        <Box>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <Select
              value={survivalSelect}
              onChange={handleSurvivalSelectChange}
              label="survival"
              defaultValue="endurance"
            >
              <MenuItem value={'challenge'}>Challenge Tower</MenuItem>
              <MenuItem value={'endurance'}>Endurance</MenuItem>
            </Select>
          </FormControl>
          {survivalSelect === 'endurance'
            ? survivalEnduranceTable(
                handleUsernameClick,
                survivalRankingsLowestScoreSort,
                survivalRankingsLongestStreakSort
              )
            : survivalChallengeTable(
                handleUsernameClick,
                survivalRankingsLowestScoreSort,
                survivalRankingsLongestStreakSort
              )}
        </Box>
      )}
    </Container>
  );
};

const survivalChallengeTable = (
  handleUsernameClick: (
    e: any,
    player: RankedUser | SurvivalRankedUser
  ) => void,
  lowestScoreList?: SurvivalRankedUser[],
  longestStreakList?: SurvivalRankedUser[]
) => (
  <Stack direction="row" spacing={2} mt={2}>
    <Stack width={'100%'}>
      {/* <Typography
        variant="h6"
        textAlign={'center'}
        fontWeight={600}
        letterSpacing={5}
        mb={1}
        sx={{ color: '#C4E4D4' }}
      >
        SURVIVAL STREAK
      </Typography> */}
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {/* <TableCell width={'3%'}></TableCell> */}
              <TableCell width={'8%'} sx={{ textAlign: 'center' }}>
                Rank
              </TableCell>
              <TableCell width={'30%'}>Player</TableCell>
              <TableCell
                width={'15%'}
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                Slain
              </TableCell>
              <TableCell
                width={'15%'}
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                Score
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {longestStreakList?.map((row, index) => (
              <TableRow key={row.profileId}>
                {/* <TableCell>^</TableCell> */}
                <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                <TableCell>
                  <Stack direction={'row'} spacing={2}>
                    <Avatar
                      src={resizeImage(row.avatarPublicId, 50, 50)}
                      variant="rounded"
                      sx={{
                        height: 25,
                        width: 25,
                      }}
                    >
                      {!row.avatarUrl ? row.username.charAt(0) : null}
                    </Avatar>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => handleUsernameClick(e, row)}
                      textAlign={'left'}
                    >
                      <Typography sx={{ lineHeight: '25px' }}>
                        {' '}
                        {row.username}
                      </Typography>
                    </Link>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  {
                    row.survivalStats?.challenge?.allTime?.longestStreak
                      ?.questionsSurvived
                  }
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  {row.survivalStats?.challenge?.allTime?.longestStreak?.score >
                  0
                    ? '+'
                    : ''}
                  {row.survivalStats?.challenge?.allTime?.longestStreak?.score}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  </Stack>
);

const survivalEnduranceTable = (
  handleUsernameClick: (
    e: any,
    player: RankedUser | SurvivalRankedUser
  ) => void,
  lowestScoreList?: SurvivalRankedUser[],
  longestStreakList?: SurvivalRankedUser[]
) => (
  <Stack direction="row" spacing={2} mt={2}>
    <Stack width={'100%'}>
      {/* <Typography
        variant="h6"
        textAlign={'center'}
        fontWeight={600}
        letterSpacing={5}
        mb={1}
        sx={{ color: '#C4E4D4' }}
      >
        SURVIVAL STREAK
      </Typography> */}
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {/* <TableCell width={'3%'}></TableCell> */}
              <TableCell width={'8%'} sx={{ textAlign: 'center' }}>
                Rank
              </TableCell>
              <TableCell width={'30%'}>Player</TableCell>
              <TableCell
                width={'15%'}
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                Slain
              </TableCell>
              <TableCell
                width={'15%'}
                sx={{
                  fontWeight: 600,
                  textAlign: 'center',
                }}
              >
                Score
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {longestStreakList?.map((row, index) => (
              <TableRow key={row.profileId}>
                {/* <TableCell>^</TableCell> */}
                <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                <TableCell>
                  <Stack direction={'row'} spacing={2}>
                    <Avatar
                      src={resizeImage(row.avatarPublicId, 50, 50)}
                      variant="rounded"
                      sx={{
                        height: 25,
                        width: 25,
                      }}
                    >
                      {!row.avatarUrl ? row.username.charAt(0) : null}
                    </Avatar>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => handleUsernameClick(e, row)}
                      textAlign={'left'}
                    >
                      <Typography sx={{ lineHeight: '25px' }}>
                        {' '}
                        {row.username}
                      </Typography>
                    </Link>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  {
                    row.survivalStats?.endurance?.longestStreak
                      ?.questionsSurvived
                  }
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                  }}
                >
                  {row.survivalStats?.endurance?.longestStreak?.score > 0
                    ? '+'
                    : ''}
                  {row.survivalStats?.endurance?.longestStreak?.score}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  </Stack>
);

const MainTable = (
  handleUsernameClick: (e: any, player: RankedUser) => void,
  cellColor: string,
  playerSeedData?: Record<string, PlayerSeedData>,
  rankings?: RankedUser[]
) => (
  <Table aria-label="simple table" size="small">
    <TableHead>
      <TableRow>
        {/* <TableCell width={'3%'}></TableCell> */}
        <TableCell width={'8%'} sx={{ textAlign: 'center' }}>
          Rank
        </TableCell>
        <TableCell width={'30%'}>Player</TableCell>
        <TableCell
          width={'15%'}
          sx={{
            color: cellColor,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          MMR
        </TableCell>
        <TableCell width={'10%'} sx={{ textAlign: 'center' }}>
          1v1 Wins
        </TableCell>
        <TableCell width={'10%'} sx={{ textAlign: 'center' }}>
          1v1 Losses
        </TableCell>
        <TableCell width={'10%'} sx={{ textAlign: 'center' }}>
          SnG Games Played
        </TableCell>
        {playerSeedData ? (
          <>
            <TableCell sx={{ textAlign: 'center' }}>Seeded (Total)</TableCell>
            <TableCell sx={{ textAlign: 'center' }}>
              Total Seeded In Range
            </TableCell>
            <TableCell sx={{ textAlign: 'center' }}>Seed Ratio</TableCell>
          </>
        ) : null}
      </TableRow>
    </TableHead>
    <TableBody>
      {rankings?.map((row, index) => (
        <TableRow key={row.profileId}>
          {/* <TableCell>^</TableCell> */}
          <TableCell sx={{ textAlign: 'center' }}>
            {row.rankedRating === -1 ? '' : index + 1}
          </TableCell>
          <TableCell>
            <Stack direction={'row'} spacing={2}>
              <Avatar
                src={resizeImage(row.avatarPublicId, 50, 50)}
                variant="rounded"
                sx={{
                  height: 25,
                  width: 25,
                }}
              >
                {!row.avatarUrl ? row.username.charAt(0) : null}
              </Avatar>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => handleUsernameClick(e, row)}
                textAlign={'left'}
              >
                <Typography sx={{ lineHeight: '25px' }}>
                  {' '}
                  {row.username}
                </Typography>
              </Link>
            </Stack>
          </TableCell>
          <TableCell
            sx={{
              textAlign: 'center',
              color: cellColor,
              fontWeight: 600,
            }}
          >
            {row.rankedRating === -1
              ? '(calibrating)'
              : Math.floor(row.rankedRating)}
          </TableCell>
          <TableCell sx={{ textAlign: 'center' }}>
            {row.rankedMatchWins || 0}
          </TableCell>
          <TableCell sx={{ textAlign: 'center' }}>
            {row.rankedMatchLosses || 0}
          </TableCell>
          <TableCell sx={{ textAlign: 'center' }}>
            {row.rankedSitAndGoGamesPlayed || 0}
          </TableCell>
          {playerSeedData ? (
            <>
              <TableCell sx={{ textAlign: 'center' }}>
                {playerSeedData[row.profileId]?.totalSeededInRange} (
                {playerSeedData[row.profileId]?.totalSeeded})
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                {playerSeedData[row.profileId]?.totalGamesInRange}
              </TableCell>
              <TableCell sx={{ textAlign: 'center' }}>
                {playerSeedData[row.profileId]?.totalGamesInRange
                  ? (
                      (playerSeedData[row.profileId]?.totalSeededInRange /
                        playerSeedData[row.profileId]?.totalGamesInRange) *
                      100
                    ).toFixed(1) + '%'
                  : '--'}
              </TableCell>
            </>
          ) : null}
        </TableRow>
      ))}
    </TableBody>
  </Table>
);
